/**
 * AssistRepository — reads and writes the two assist tables. SQL only (rule R3).
 *
 * media_assist holds what the free screening pass found on every shoot;
 * media_focus holds what the opted-into review measured from the originals.
 * Two tables because they are produced at different moments, at different
 * resolutions, and are invalidated by different things.
 */

import type { SqliteClient } from '../adapters/sqlite-client';

export interface AssistRow {
  mediaId: number;
  sharpness: number;
  isSoft: boolean;
  burstId: number | null;
  isPick: boolean;
  model: string | null;
  scoredAt: number;
}

/** What the UI needs to draw the two markers, keyed by media id. */
export type AssistMarkers = Map<
  number,
  { isSoft: boolean; isPick: boolean; burstId: number | null; sharpness: number }
>;

export class AssistRepository {
  constructor(private readonly db: SqliteClient) {}

  /** Every marker for a shoot, in one query — the filmstrip reads this once. */
  markersFor(projectId: number): AssistMarkers {
    const rows = this.db.all<{
      media_id: number;
      is_soft: number;
      is_pick: number;
      burst_id: number | null;
      sharpness: number;
    }>(
      `SELECT a.media_id, a.is_soft, a.is_pick, a.burst_id, a.sharpness
         FROM media_assist a
         JOIN media m ON m.id = a.media_id
        WHERE m.project_id = ?`,
      [projectId],
    );

    return new Map(
      rows.map((r) => [
        Number(r.media_id),
        {
          isSoft: Number(r.is_soft) === 1,
          isPick: Number(r.is_pick) === 1,
          burstId: r.burst_id === null ? null : Number(r.burst_id),
          // Carried through so the filmstrip can sort by it without a second
          // query per frame; it is one number that is already in the row.
          sharpness: Number(r.sharpness),
        },
      ]),
    );
  }

  /** Sharpness for every scored frame in a shoot, in canonical order. */
  scoresFor(projectId: number): { id: number; sharpness: number }[] {
    return this.db
      .all<{ id: number; sharpness: number }>(
        `SELECT a.media_id AS id, a.sharpness
           FROM media_assist a
           JOIN media m ON m.id = a.media_id
          WHERE m.project_id = ?
          ORDER BY m.rel_path COLLATE NOCASE ASC, m.id ASC`,
        [projectId],
      )
      .map((r) => ({ id: Number(r.id), sharpness: Number(r.sharpness) }));
  }

  /** Ids in a shoot that have no sharpness score yet — the work queue. */
  unscored(projectId: number, limit: number): number[] {
    return this.db
      .all<{ id: number }>(
        `SELECT m.id
           FROM media m
      LEFT JOIN media_assist a ON a.media_id = m.id
          WHERE m.project_id = ? AND m.type = 'image' AND m.status = 'ok' AND a.media_id IS NULL
          ORDER BY m.rel_path COLLATE NOCASE ASC, m.id ASC
          LIMIT ?`,
        [projectId, limit],
      )
      .map((r) => Number(r.id));
  }

  /** Frames scored but still missing an embedding for the current model. */
  needingEmbedding(projectId: number, model: string, limit: number): number[] {
    return this.db
      .all<{ id: number }>(
        `SELECT a.media_id AS id
           FROM media_assist a
           JOIN media m ON m.id = a.media_id
          WHERE m.project_id = ?
            AND (a.embedding IS NULL OR a.model IS NOT ?)
          ORDER BY m.rel_path COLLATE NOCASE ASC, m.id ASC
          LIMIT ?`,
        [projectId, model, limit],
      )
      .map((r) => Number(r.id));
  }

  /** Stored embeddings for a shoot, in canonical order, for burst detection. */
  embeddingsFor(projectId: number, model: string): { id: number; vector: Float32Array }[] {
    const rows = this.db.all<{ id: number; embedding: Uint8Array }>(
      `SELECT a.media_id AS id, a.embedding
         FROM media_assist a
         JOIN media m ON m.id = a.media_id
        WHERE m.project_id = ? AND a.embedding IS NOT NULL AND a.model IS ?
        ORDER BY m.rel_path COLLATE NOCASE ASC, m.id ASC`,
      [projectId, model],
    );

    return rows.map((r) => ({
      id: Number(r.id),
      // The BLOB round-trips as bytes; reinterpret them as the float vector.
      vector: new Float32Array(
        r.embedding.buffer.slice(r.embedding.byteOffset, r.embedding.byteOffset + r.embedding.byteLength),
      ),
    }));
  }

  /** Frames still missing a focus measurement taken by the current measure. */
  needingFocus(projectId: number, tag: string, limit: number): number[] {
    return this.db
      .all<{ id: number }>(
        `SELECT m.id
           FROM media m
      LEFT JOIN media_focus f ON f.media_id = m.id
          WHERE m.project_id = ? AND m.type = 'image' AND m.status = 'ok'
            AND (f.media_id IS NULL OR f.tag IS NOT ?)
          ORDER BY m.rel_path COLLATE NOCASE ASC, m.id ASC
          LIMIT ?`,
        [projectId, tag, limit],
      )
      .map((r) => Number(r.id));
  }

  /**
   * Focus for every measured frame in a shoot, in canonical order.
   *
   * Filtered by tag, so a shoot half-measured by an older version of the measure
   * never contributes numbers on a different scale to the same comparison.
   */
  focusFor(projectId: number, tag: string): { id: number; sharpness: number }[] {
    return this.db
      .all<{ id: number; sharpness: number }>(
        `SELECT f.media_id AS id, f.sharpness
           FROM media_focus f
           JOIN media m ON m.id = f.media_id
          WHERE m.project_id = ? AND f.tag IS ?
          ORDER BY m.rel_path COLLATE NOCASE ASC, m.id ASC`,
        [projectId, tag],
      )
      .map((r) => ({ id: Number(r.id), sharpness: Number(r.sharpness) }));
  }

  putFocus(mediaId: number, sharpness: number, tag: string, now: number): void {
    this.db.run(
      `INSERT INTO media_focus (media_id, sharpness, tag, measured_at) VALUES (?, ?, ?, ?)
       ON CONFLICT(media_id) DO UPDATE SET
         sharpness = excluded.sharpness, tag = excluded.tag, measured_at = excluded.measured_at`,
      [mediaId, sharpness, tag, now],
    );
  }

  putSharpness(mediaId: number, sharpness: number, now: number): void {
    this.db.run(
      `INSERT INTO media_assist (media_id, sharpness, scored_at) VALUES (?, ?, ?)
       ON CONFLICT(media_id) DO UPDATE SET sharpness = excluded.sharpness, scored_at = excluded.scored_at`,
      [mediaId, sharpness, now],
    );
  }

  putEmbedding(mediaId: number, vector: Float32Array, model: string): void {
    const bytes = new Uint8Array(vector.buffer, vector.byteOffset, vector.byteLength);
    this.db.run('UPDATE media_assist SET embedding = ?, model = ? WHERE media_id = ?', [
      bytes,
      model,
      mediaId,
    ]);
  }

  /** Rewrites the soft flags for a whole shoot; they are relative to it. */
  setSoft(projectId: number, softIds: Set<number>): void {
    this.db.transaction(() => {
      this.db.run(
        `UPDATE media_assist SET is_soft = 0
          WHERE media_id IN (SELECT id FROM media WHERE project_id = ?)`,
        [projectId],
      );
      for (const id of softIds) {
        this.db.run('UPDATE media_assist SET is_soft = 1 WHERE media_id = ?', [id]);
      }
    });
  }

  /** Rewrites bursts and picks together — one is meaningless without the other. */
  setBurstsAndPicks(
    projectId: number,
    bursts: Map<number, number>,
    pickIds: Set<number>,
  ): void {
    this.db.transaction(() => {
      this.db.run(
        `UPDATE media_assist SET burst_id = NULL, is_pick = 0
          WHERE media_id IN (SELECT id FROM media WHERE project_id = ?)`,
        [projectId],
      );
      for (const [mediaId, burstId] of bursts) {
        this.db.run('UPDATE media_assist SET burst_id = ?, is_pick = ? WHERE media_id = ?', [
          burstId,
          pickIds.has(mediaId) ? 1 : 0,
          mediaId,
        ]);
      }
    });
  }

  /** How far along a shoot's review is. */
  progress(
    projectId: number,
    focusTag = '',
  ): { total: number; scored: number; embedded: number; focused: number } {
    const row = this.db.get<{ total: number; scored: number; embedded: number; focused: number }>(
      `SELECT
         (SELECT COUNT(*) FROM media WHERE project_id = ? AND type = 'image' AND status = 'ok') AS total,
         (SELECT COUNT(*) FROM media_assist a JOIN media m ON m.id = a.media_id
           WHERE m.project_id = ?) AS scored,
         (SELECT COUNT(*) FROM media_assist a JOIN media m ON m.id = a.media_id
           WHERE m.project_id = ? AND a.embedding IS NOT NULL) AS embedded,
         (SELECT COUNT(*) FROM media_focus f JOIN media m ON m.id = f.media_id
           WHERE m.project_id = ? AND f.tag IS ?) AS focused`,
      [projectId, projectId, projectId, projectId, focusTag],
    );
    return {
      total: Number(row?.total ?? 0),
      scored: Number(row?.scored ?? 0),
      embedded: Number(row?.embedded ?? 0),
      focused: Number(row?.focused ?? 0),
    };
  }

  /** Forgets both tables together — one is meaningless without the other. */
  clear(projectId: number): void {
    this.db.transaction(() => {
      const where = 'media_id IN (SELECT id FROM media WHERE project_id = ?)';
      this.db.run(`DELETE FROM media_assist WHERE ${where}`, [projectId]);
      this.db.run(`DELETE FROM media_focus WHERE ${where}`, [projectId]);
    });
  }
}
